import { CurrencyPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import { RouterLink } from '@angular/router';
import { lastValueFrom, of, switchMap, take } from 'rxjs';
import { Cart, CartLine } from 'src/app/classes/utility';
import { EnvironmentService } from 'src/app/service/environment.service';
import { MercuryService } from 'src/app/service/mercury.service';
import { ShopifyService } from 'src/app/service/shopify.service';
import { StoryBlokService } from 'src/app/service/storyblok.service';
import { ThemeService } from 'src/app/service/theme.service';
import { ConditionalUpsellComponent } from './conditional-upsell/conditional-upsell.component';
import { LineitemComponent } from './lineitem/lineitem.component';
import { MotivatorComponent } from './motivator/motivator.component';
import { ShippingToggleComponent } from './shipping-toggle/shipping-toggle.component';

@Component({
  selector: 'app-minicart',
  standalone: true,
  imports: [
    LineitemComponent,
    NgFor,
    NgIf,
    RouterLink,
    CurrencyPipe,
    NgClass,
    MotivatorComponent,
    ShippingToggleComponent,
    ConditionalUpsellComponent,
  ],
  templateUrl: './minicart.component.html',
  styleUrl: './minicart.component.scss',
})
export class MinicartComponent implements OnInit {
  checkout: Cart | any;

  @Input() theme!: any;
  _totalQty: number = 0;
  _upsellMode: boolean = false;
  _upsell: any = [];
  _processing: boolean = false;

  constructor(
    private shopifyService: ShopifyService,
    private renderer: Renderer2,
    private el: ElementRef,
    private themeService: ThemeService,
    private storyblokService: StoryBlokService,
    private environmentService: EnvironmentService,
    private mercuryService: MercuryService
  ) {
    this.storyblokService.themeObservable.subscribe((data) => {
      this.theme = data;
    });
  }

  isBelowMaximumThreshold(giftMode: any) {
    return giftMode.maximumThreshold != null
      ? giftMode.maximumThreshold >
          parseFloat(this.checkout.cost.totalAmount.amount)
      : true;
  }

  isAboveMinimumThreshold(giftMode: any) {
    return (
      giftMode.minimumTreshold <
      parseFloat(this.checkout.cost.totalAmount.amount)
    );
  }

  isGiftPresentInCart(giftMode: any) {
    if (!this.checkout.lines.edges) return;
    return this.checkout.lines.edges
      .map((x: any) => x.node)
      .find(
        (y: CartLine) =>
          y.merchandise.product.handle == giftMode.handle &&
          y.attributes.find((x) => x.key == '_freegift')
      );
  }

  allGiftsPresentInCart() {
    return this.checkout.lines.edges
      .map((x: any) => x.node)
      .filter((y: CartLine) => y.attributes.find((x) => x.key == '_freegift'));
  }

  allIneligibleGiftsInCart(_config: any) {
    return this.checkout.lines.edges.filter((cartLine: any) => {
      const freeGiftAttribute = cartLine.node.attributes.find(
        (attr: any) => attr.key == '_freegift'
      );

      if (!freeGiftAttribute) return false; // Not a free gift

      const giftMode = this.getGiftModeByHandle(
        cartLine.node.merchandise.product.handle,
        _config
      );
      if (!giftMode) return true; // If no matching giftMode, it's ineligible

      return !this.isThisASuitableFreeGift(giftMode); // Check suitability
    });
  }

  getGiftModeByHandle(handle: string, _config: any) {
    return _config.gwp.offers.find(
      (giftMode: any) => giftMode.handle === handle
    );
  }

  isThisASuitableFreeGift(giftMode: any) {
    if (this.isGiftPresentInCart(giftMode)) return false;

    return (
      this.isBelowMaximumThreshold(giftMode) &&
      this.isAboveMinimumThreshold(giftMode)
    );
  }

  isSuitableGiftToAddToCart(data: any) {
    if (
      data.variants[0].inventory_management !== null &&
      data.variants[0].inventory_policy === 'deny' &&
      data.variants[0].inventory_quantity < 1
    ) {
      console.error('GIFT NO STOCK: ' + data.title + ' ' + data.handle);
      return false;
    }
    if (data.variants.length > 1 || parseFloat(data.variants[0].price) !== 0) {
      console.error('GIFT NOT SUITABLE: ' + data.title + ' ' + data.handle);
      return false;
    }
    return true;
  }

  async addGiftToCart(offer: any) {
    await lastValueFrom(
      this.mercuryService.getProductByHandle(offer.handle)
    ).then(async (data) => {
      await lastValueFrom(
        this.shopifyService.addToCart([
          {
            variantId: data.variants[0].admin_graphql_api_id,
            quantity: 1,
            attributes: [{ key: '_freegift', value: 'true' }],
          },
        ])
      );
    });
  }

  async ngOnInit() {
    if (!this.environmentService.isBrowser()) return;

    const _config = await this.shopifyService.getMetaobject();
    try {
      if (_config?.gwp?.enabled) {
        for (const offer of _config.gwp.offers) {
          offer.minimumTreshold = offer.minimumTreshold
            ? offer.minimumTreshold / 100
            : undefined;
          offer.maximumThreshold = offer.maximumThreshold
            ? offer.maximumThreshold / 100
            : undefined;
        }
      }
    } catch (err) {
      console.log(err);
    }

    this.shopifyService.cartObservable.subscribe(async (cart: Cart | any) => {
      this.checkout = { ...structuredClone(cart) };

      if (this.checkout?.state === 'loading') return;

      try {
        if (this.checkout?.id) {
          this.themeService.viewCart(this.checkout);
        }
      } catch (err) {
        console.error(err);
      }
      if (
        !this.checkout?.lines?.edges ||
        this.checkout.lines.edges.length === 0
      )
        return;

      try {
        if (this.checkout?.lines) {
          this.mercuryService
            .getProgrammaticZone('cart', this.checkout)
            .subscribe((data) => {
              this._upsell = data?.products ?? [];
            });
        }
      } catch (err) {
        console.error(err);
      }

      if (_config?.gwp?.enabled) {
        for (const offer of _config.gwp.offers) {
          if (this.isThisASuitableFreeGift(offer)) {
            await this.addGiftToCart(offer);
            return;
          }
        }
      }

      if (!this._processing) {
        console.log(_config);
        if (_config) {
          const matching = this.allIneligibleGiftsInCart(_config).map(
            (x: any) => x.node.id
          );
          if (matching?.length > 0) {
            this._processing = true;
            await lastValueFrom(this.shopifyService.removeFromCart(matching));
          }
        } else {
          const matching = this.allGiftsPresentInCart().map(
            (x: any) => x.node.id
          );
          if (matching?.length > 0) {
            this._processing = true;
            await lastValueFrom(this.shopifyService.removeFromCart(matching));
          }
        }
      } else {
        this._processing = false;
      }

      this._totalQty = this.checkout.lines.edges
        .map((x: any) => x.node)
        .filter((x: any) =>
          x.merchandise.product.tags.includes('discountapplies')
        )
        .reduce((acc: number, x: any) => acc + x.quantity, 0);

      this.updateCartPrices(this.checkout.lines.edges.map((x: any) => x.node));

      const unmatchedItems = this.findUnmatchedEntries(
        structuredClone(this.checkout.lines.edges.map((x: any) => x.node))
      );
      if (unmatchedItems.length > 0) {
        await lastValueFrom(this.shopifyService.removeFromCart(unmatchedItems));
      }

      const zeroStockItems = this.findZeroQuantityEntries(
        structuredClone(this.checkout.lines.edges.map((x: any) => x.node))
      );
      if (zeroStockItems.length > 0) {
        await lastValueFrom(this.shopifyService.removeFromCart(zeroStockItems));
      }

      if (this.theme?.content?.toggle_product) {
        this.checkout.lines.edges = this.checkout.lines.edges.filter(
          (x: any) =>
            !x.node.merchandise.product.handle.includes(
              this.theme.content.toggle_product
            ) && !x.node.merchandise.product.title.includes('Personalisation')
        );
      }
    });

    this.themeService.themeEvents.subscribe((data) => {
      if (data.type == 'clear' || data.type == 'menuClose') {
        this.renderer.setStyle(this.el.nativeElement, 'opacity', '0');
        this.renderer.removeClass(this.el.nativeElement, 'active');
      }
      if (data.type == 'minicartOpen') {
        this.renderer.setStyle(this.el.nativeElement, 'opacity', '1');
        this.renderer.addClass(this.el.nativeElement, 'active');
        this.shopifyService
          .getCart()
          .pipe(
            take(1),
            switchMap((data: any) => {
              if (!(data as Cart)?.id) {
                return this.shopifyService.updateCart();
              }
              return of(data); // Return the data if it has an ID
            })
          )
          .subscribe();
      }
    });
  }

  clickedOutside($event: any) {
    this.themeService.clear();
  }

  close() {
    this.themeService.clear();
  }

  findZeroQuantityEntries(cartLines: CartLine[]): string[] {
    let ids = [];
    for (let line of cartLines) {
      if (line.quantity == 0) {
        ids.push(line.id);
      }
    }
    return ids;
  }

  findUnmatchedEntries(cartLines: CartLine[]): string[] {
    // Create a map to track personalisation keys
    const personalisationMap: { [key: string]: string[] } = {};
    const unmatchedIds: string[] = [];

    // Populate the map with personalisation keys and their corresponding IDs
    cartLines.forEach((line) => {
      const personalisationAttr = line.attributes.find(
        (attr: any) => attr.key === '_Personalisation'
      );
      if (personalisationAttr) {
        const personalisationKey = personalisationAttr.value;
        if (!personalisationMap[personalisationKey]) {
          personalisationMap[personalisationKey] = [];
        }
        personalisationMap[personalisationKey].push(line.id);
      }
    });

    // Check for lines that have the "Personalised" attribute
    cartLines.forEach((line) => {
      const personalisedAttr = line.attributes.find(
        (attr: any) => attr.key === 'Personalised'
      );
      const personalisationAttr = line.attributes.find(
        (attr: any) => attr.key === '_Personalisation'
      );
      if (personalisedAttr && personalisationAttr) {
        const personalisationKey = personalisationAttr.value;
        if (personalisationMap[personalisationKey].length < 2) {
          unmatchedIds.push(line.id);
        }
      }
    });

    // Find entries that have "_Personalisation" key but no matching "Personalised" counterpart
    for (const key in personalisationMap) {
      if (personalisationMap[key].length < 2) {
        unmatchedIds.push(...personalisationMap[key]);
      }
    }

    // Remove duplicates from unmatchedIds
    const uniqueUnmatchedIds = Array.from(new Set(unmatchedIds));

    return uniqueUnmatchedIds;
  }

  updateCartPrices(cartLines: CartLine[]) {
    for (let line of cartLines) {
      if (line.attributes.some((attr: any) => attr.key === 'Personalised')) {
        let personalisationId = line.attributes.find(
          (attr: any) => attr.key === '_Personalisation'
        )?.value;
        const matchingPersonalisationLineItem = cartLines.find((x) => {
          return (
            x.attributes.find((attr: any) => attr.key === '_Personalisation')
              ?.value == personalisationId || false
          );
        });
        if (matchingPersonalisationLineItem) {
          line.cost.subtotalAmount.amount = this.addFloatStrings(
            line.cost.subtotalAmount.amount,
            matchingPersonalisationLineItem.cost.subtotalAmount.amount
          );
          line.cost.totalAmount.amount = this.addFloatStrings(
            line.cost.totalAmount.amount,
            matchingPersonalisationLineItem.cost.totalAmount.amount
          );
        }
      }
    }
  }

  addFloatStrings(floatStr1: string, floatStr2: string) {
    const parts1 = floatStr1.split('.');
    const parts2 = floatStr2.split('.');

    // Convert integer parts to numbers
    const intPart1 = parseInt(parts1[0], 10) || 0;
    const intPart2 = parseInt(parts2[0], 10) || 0;

    // Convert fractional parts to numbers
    const fracPart1 = parts1[1] ? parseInt(parts1[1], 10) : 0;
    const fracPart2 = parts2[1] ? parseInt(parts2[1], 10) : 0;

    // Find the maximum length of fractional parts
    const maxFracLength = Math.max(
      parts1[1]?.length || 0,
      parts2[1]?.length || 0
    );

    // Normalize fractional parts to the same length
    const normFracPart1 =
      fracPart1 * Math.pow(10, maxFracLength - (parts1[1]?.length || 0));
    const normFracPart2 =
      fracPart2 * Math.pow(10, maxFracLength - (parts2[1]?.length || 0));

    // Add integer parts and fractional parts
    const intSum = intPart1 + intPart2;
    const fracSum = normFracPart1 + normFracPart2;

    // Normalize the fractional sum back to the correct decimal place
    const fracSumStr = fracSum.toString().padStart(maxFracLength, '0');

    // Handle carry-over from fractional part to integer part
    const carryOver = Math.floor(fracSum / Math.pow(10, maxFracLength));
    const finalIntSum = intSum + carryOver;
    const finalFracSum = fracSum % Math.pow(10, maxFracLength);

    return `${finalIntSum}.${finalFracSum
      .toString()
      .padStart(maxFracLength, '0')}`;
  }
}
